import { IoConstruct, IoDocumentText, IoFlame, IoLockClosed, IoWarning } from "react-icons/io5";
import { LuScissorsLineDashed } from "react-icons/lu";
import { FaSquarePersonConfined } from "react-icons/fa6";

/**
 * The icons a form template can carry.
 *
 * The stored value is the `key` — never the glyph or the colour. The technician
 * app maps the same keys to Ionicons through react-native-vector-icons, so the
 * two apps show the same glyph without sharing code, and either side can restyle
 * without a migration.
 *
 * Adding one means adding it here, in the technician app's formIcons.js, and to
 * FORM_ICON_KEYS in wo-form-template.service.ts — the server rejects keys it
 * does not know, so a template cannot end up pointing at a glyph that only one
 * app can draw.
 */
export const FORM_ICONS = [
  { key: "attention", label: "Attention", color: "#F5A623", Icon: IoWarning },
  { key: "lockout", label: "Lockout / Tagout", color: "#DC2626", Icon: IoLockClosed },
  // Scissors cutting a dashed line — a break *in a line*. A broken chain link
  // read as a severed connection, which is a different idea.
  { key: "lineBreak", label: "Line Break", color: "#0058E5", Icon: LuScissorsLineDashed },
  { key: "hotWork", label: "Hot Work", color: "#EA580C", Icon: IoFlame },
  { key: "parts", label: "Parts Request", color: "#0F9488", Icon: IoConstruct },
  // The standard confined-space pictogram: a person inside a square.
  { key: "confinedSpace", label: "Confined Space", color: "#7C3AED", Icon: FaSquarePersonConfined },
];

// Templates with no icon fall back to a neutral document glyph rather than a
// gap, so rows stay aligned.
export const DEFAULT_FORM_ICON = {
  key: null,
  label: "None",
  color: "#8A93A6",
  Icon: IoDocumentText,
};

export const getFormIcon = (key) =>
  FORM_ICONS.find((icon) => icon.key === key) || DEFAULT_FORM_ICON;

/** The glyph for a template or form row, at a given size. */
export const FormIcon = ({ iconKey, size = 18 }) => {
  const { Icon, color, label } = getFormIcon(iconKey);
  return <Icon size={size} color={color} title={label} />;
};
